

import { Component, Vue, Watch, Prop, InjectReactive, Inject } from "vue-property-decorator"
import { namespace, Getter, Action, Mutation } from "vuex-class"

import vSelect from "vue-select"
import CaseFileDisplay from "./CaseFileDisplay.vue"
import Confirmation from "../../Confirmation.vue"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { CaseDetails, CaseField, CaseAudit } from "@/types"
import CaseFile, { FilePendingUpload } from "@/types/caseFile"
import { LayoutLocations } from "@/types/templateLayout"

import api from "@/services/api"
import FileService from "@/services/file-service"
import { getXhrErrorMessage } from "@/scripts/xhrErrorMessage"

const caseStore = namespace("cases")

const errorToastDelayMs = 10000

@Component({
	components: {
		"case-file": CaseFileDisplay,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"confirm": Confirmation
	}
})
export default class CaseFileUpload extends Vue {
	@Inject("fileService") fileService!: FileService;

	@Getter("getUseV2") private usingV2!: boolean;

	$refs!: {
		confirmModal: Confirmation
	}

	@Prop({ type: Boolean, default: false })
	public creatingNewCase!: boolean;

	@Prop({ type: String || null, default: null })
	public fieldId!: string | null;

	@Prop({ type: String || null, default: null })
	public fieldName!: string | null;

	@Prop({ type: Boolean, default: false })
	public showUploadingFiles!: boolean;

	@Prop({ type: Boolean, default: false })
	public displayAsList!: boolean;

	@Prop({ type: Boolean, default: false })
	viewOnly!: boolean;

	@Prop()
	public caseFiles!: CaseFile[];

	@Prop({ default: null })
	public layout!: LayoutLocations | null;

	private uploadFieldName: any = "files";
	private dragging: boolean = false;
	private uploadSupportPageUrl: string | undefined = process.env.VUE_APP_UPLOADS_SUPPORT_PAGE_URI;

	public get uploadingFiles () {
		if (!this.fileService) {
			return []
		}
		return this.fileService.uploadFiles.filter((file: any) => file.fieldId === this.fieldId)
	}

	/**
	 * The V2 main content is wide enough to sit files side by side. The right-hand menu
	 * and the compact list display are not, so they stay in a single column.
	 */
	private get useFileGrid (): boolean {
		return this.usingV2 &&
			!this.displayAsList &&
			this.layout !== LayoutLocations.RightHandMenu
	}

	private get failedUploads (): FilePendingUpload[] {
		return this.uploadingFiles.filter((file: FilePendingUpload) => file.failed)
	}

	@Watch("failedUploads", { immediate: true })
	private failedUploadsChanged (files: FilePendingUpload[]): void {
		files.filter(file => !file.errorShown).forEach(file => this.reportUploadFailure(file))
	}

	private async onChange (event: any): Promise<void> {
		this.dragging = false
		if (this.creatingNewCase) {
			return
		}

		if (this.viewOnly) return

		let files = (event.target.files || event.dataTransfer.files) as any[]

		const uploadFiles: any[] = []
		files.forEach(file => {
			const newFile = {
				fieldId: this.fieldId,
				name: file.name,
				fileData: file,
				uploading: false,
				uploadPercentage: 0,
				failed: false,
				success: false,
				error: null,
				uploadRun: false,
				errorShown: false
			}

			uploadFiles.push(newFile)
		})

		// this.uploadFiles({ fieldId: this.fieldId, uploadFiles: uploadFiles })
		this.fileService.addFiles(uploadFiles)
	}

	deleteFile (file: CaseFile) {
		this.$refs.confirmModal.open(file.filename, () => {
			this.fileService.deleteFile(file.id, this.fieldId)
		})
	}

	private failureReason (file: FilePendingUpload): string | null {
		return getXhrErrorMessage(file.error)
	}

	private reportUploadFailure (file: FilePendingUpload): void {
		file.errorShown = true

		console.error(`File upload failed for "${file.name}"`, file.error)

		// Server errors can run to a full stack trace, so only the short reason is shown.
		const reason = getXhrErrorMessage(file.error)
		const message = reason
			? `File upload failed for "${file.name}": ${reason}`
			: `File upload failed for "${file.name}".`

		this.$bvToast.toast(message, {
			title: "Upload Error",
			variant: "danger",
			solid: true,
			autoHideDelay: errorToastDelayMs
		})
	}
}
